import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import {
  MessageCircle, GraduationCap, ShoppingBag, Check, Package,
  FileCheck, Ship, Shield, Headset, ArrowRight, Globe, Users,
  Send, CheckCircle, Loader2, AlertCircle, ShieldCheck,
} from "lucide-react";
import AnimatedSection from "./AnimatedSection";

const WHATSAPP_URL = "https://wa.me/34672808030?text=Hola%20OKontainers!%20Soy%20de%20una%20academia%20de%20ecommerce%20y%20me%20interesa%20la%20colaboraci%C3%B3n.";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AcademyLandingPage = () => {
  const { lang, toggleLang } = useLang();
  const [form, setForm] = useState({ name: "", company: "", email: "" });
  const [status, setStatus] = useState("idle");

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "OKontainers para Academias | Amazon FBA & Ecommerce";
    let canon = document.querySelector('link[rel="canonical"]');
    if (!canon) { canon = document.createElement("link"); canon.setAttribute("rel", "canonical"); document.head.appendChild(canon); }
    canon.setAttribute("href", "https://okontainers.com/academias");
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.company || !form.email) return;
    setStatus("loading");
    try {
      const res = await fetch(`${BACKEND_URL}/api/audit-request`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, source: "academy-landing" }),
      });
      if (res.ok) { setStatus("success"); setForm({ name: "", company: "", email: "" }); }
      else setStatus("error");
    } catch { setStatus("error"); }
  };

  return (
    <div className="bg-white" data-testid="academy-landing-page">
      {/* Nav minimal */}
      <nav className="fixed top-0 left-0 right-0 z-50 nav-glass-scrolled">
        <div className="max-w-7xl mx-auto px-5 md:px-10 flex items-center justify-between h-16">
          <a href="/" className="flex items-center gap-2 shrink-0">
            <img src="/logo.png" alt="OKontainers" className="h-[86px] md:h-[108px] w-auto object-contain" />
          </a>
          <div className="flex items-center gap-3">
            <button onClick={toggleLang} data-testid="academy-lang-toggle"
              className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-navy transition-colors px-3 py-1.5 rounded-full border border-slate-200">
              <Globe size={14} />{lang === "es" ? "EN" : "ES"}
            </button>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer"
              className="hidden md:inline-flex items-center gap-2 bg-electric text-white text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-electric-hover transition-all shadow-lg shadow-electric/20">
              <MessageCircle size={16} />WhatsApp
            </a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section data-testid="academy-hero" className="relative bg-navy min-h-[80vh] flex items-center overflow-hidden pt-20">
        <div className="absolute inset-0 hero-grid opacity-40" />
        <div className="hero-orb hero-orb-1" />
        <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-28">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}
            className="inline-flex items-center gap-2 bg-electric/10 border border-electric/20 rounded-full px-4 py-1.5 mb-6">
            <GraduationCap size={16} className="text-electric-light" />
            <span className="text-electric-light text-xs font-semibold uppercase tracking-wider font-body">
              {lang === "es" ? "PROGRAMA PARA ACADEMIAS" : "ACADEMY PROGRAM"}
            </span>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.4 }}
            className="text-4xl sm:text-5xl md:text-6xl font-black text-white leading-[1.05] tracking-tight font-heading mb-6 max-w-3xl">
            {lang === "es"
              ? "Que tus Alumnos Importen sin Errores desde el D\u00eda 1"
              : "Let Your Students Import Without Mistakes from Day 1"}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.7 }}
            className="text-lg md:text-xl text-slate-300 leading-relaxed max-w-2xl mb-10 font-body">
            {lang === "es"
              ? "Somos el departamento de importaci\u00f3n que tus alumnos de Amazon FBA y ecommerce necesitan. Ellos venden, nosotros nos encargamos de traer la mercanc\u00eda."
              : "We\u2019re the import department your Amazon FBA and ecommerce students need. They sell, we bring the goods."}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 1.0 }}
            className="flex flex-col sm:flex-row gap-4">
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="academy-hero-cta"
              className="inline-flex items-center justify-center gap-2.5 bg-electric text-white font-semibold py-4 px-8 rounded-full hover:bg-electric-hover transition-all shadow-lg shadow-electric/30 text-base md:text-lg">
              <MessageCircle size={20} />
              {lang === "es" ? "Hablemos de Colaboraci\u00f3n" : "Let\u2019s Talk Collaboration"}
            </a>
            <button onClick={() => document.getElementById("academy-benefits")?.scrollIntoView({ behavior: "smooth" })}
              className="inline-flex items-center justify-center gap-2 bg-white/10 text-white font-medium py-4 px-8 rounded-full hover:bg-white/20 transition-all border border-white/15 text-base md:text-lg">
              {lang === "es" ? "Ver Ventajas" : "See Benefits"} <ArrowRight size={18} />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Problem for academies */}
      <section className="bg-slate-50 py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <AnimatedSection>
            <h2 className="text-3xl md:text-4xl font-bold text-navy font-heading mb-4 max-w-2xl">
              {lang === "es"
                ? "El Problema que Tienen tus Alumnos"
                : "The Problem Your Students Have"}
            </h2>
            <p className="text-slate-500 font-body mb-10 max-w-xl">
              {lang === "es"
                ? "Aprenden a vender en Amazon, pero cuando llega el momento de importar..."
                : "They learn to sell on Amazon, but when it\u2019s time to import..."}
            </p>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {(lang === "es" ? [
              { t: "No saben c\u00f3mo negociar con f\u00e1bricas", d: "Pagan de m\u00e1s, aceptan condiciones malas y no saben pedir muestras." },
              { t: "Se pierden con la log\u00edstica", d: "No entienden FCL vs LCL, no saben qu\u00e9 es un B/L ni c\u00f3mo funciona la aduana." },
              { t: "Cometen errores costosos", d: "Documentaci\u00f3n mal, etiquetado incorrecto, mercanc\u00eda retenida en aduana." },
            ] : [
              { t: "Don\u2019t know how to negotiate with factories", d: "They overpay, accept bad terms, and don\u2019t know how to request samples." },
              { t: "Get lost with logistics", d: "They don\u2019t understand FCL vs LCL, B/L, or how customs works." },
              { t: "Make costly mistakes", d: "Wrong documentation, incorrect labeling, goods stuck in customs." },
            ]).map((item, i) => (
              <AnimatedSection key={i} delay={i * 0.1}>
                <div className="bg-navy rounded-2xl p-6 text-white h-full">
                  <h3 className="text-base font-bold mb-2 font-heading">{item.t}</h3>
                  <p className="text-slate-300 text-sm font-body">{item.d}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section id="academy-benefits" className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <AnimatedSection>
            <h2 className="text-3xl md:text-4xl font-bold text-navy font-heading mb-12">
              {lang === "es" ? "Lo Que Ofrecemos a tu Academia" : "What We Offer Your Academy"}
            </h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* For students */}
            <AnimatedSection delay={0.1}>
              <div data-testid="academy-students-card" className="bento-card h-full">
                <div className="w-11 h-11 rounded-xl bg-electric/10 flex items-center justify-center mb-5">
                  <ShoppingBag size={22} className="text-electric" />
                </div>
                <h3 className="text-xl font-bold text-navy mb-5 font-heading">
                  {lang === "es" ? "Para tus Alumnos" : "For Your Students"}
                </h3>
                <div className="space-y-3">
                  {(lang === "es" ? [
                    "Negociamos precios, packaging y etiquetado con su f\u00e1brica",
                    "Coordinamos transporte hasta Amazon FBA o su almac\u00e9n",
                    "Gestionamos toda la documentaci\u00f3n y aduanas",
                    "Resolvemos cualquier incidencia durante el proceso",
                    "Feedback diario del estado de cada operaci\u00f3n",
                  ] : [
                    "We negotiate prices, packaging, and labeling with their factory",
                    "We coordinate transport to Amazon FBA or their warehouse",
                    "We handle all documentation and customs",
                    "We resolve any issues during the process",
                    "Daily feedback on every operation\u2019s status",
                  ]).map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <Check size={16} className="text-emerald-500 shrink-0 mt-0.5" />
                      <span className="text-slate-600 text-sm font-body">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedSection>

            {/* For academy */}
            <AnimatedSection delay={0.2}>
              <div data-testid="academy-collab-card" className="bento-card h-full">
                <div className="w-11 h-11 rounded-xl bg-electric/10 flex items-center justify-center mb-5">
                  <GraduationCap size={22} className="text-electric" />
                </div>
                <h3 className="text-xl font-bold text-navy mb-5 font-heading">
                  {lang === "es" ? "Para tu Academia" : "For Your Academy"}
                </h3>
                <div className="space-y-3">
                  {(lang === "es" ? [
                    "Ofr\u00e9celes un servicio profesional de importaci\u00f3n desde el d\u00eda 1",
                    "Condiciones especiales para tu comunidad de alumnos",
                    "Modelo de colaboraci\u00f3n flexible: comisiones, referidos o marca blanca",
                    "Tus alumnos obtienen mejores resultados y t\u00fa ganas reputaci\u00f3n",
                    "Soporte directo por WhatsApp para ti y para ellos",
                  ] : [
                    "Offer a professional import service from day 1",
                    "Special conditions for your student community",
                    "Flexible collaboration: commissions, referrals, or white-label",
                    "Your students get better results and you gain reputation",
                    "Direct WhatsApp support for you and them",
                  ]).map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <Check size={16} className="text-emerald-500 shrink-0 mt-0.5" />
                      <span className="text-slate-600 text-sm font-body">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* How it works for academies */}
      <section className="bg-slate-50 py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-5 md:px-10">
          <AnimatedSection>
            <h2 className="text-3xl md:text-4xl font-bold text-navy font-heading mb-12">
              {lang === "es" ? "C\u00f3mo Funciona la Colaboraci\u00f3n" : "How the Collaboration Works"}
            </h2>
          </AnimatedSection>
          <div className="space-y-6">
            {(lang === "es" ? [
              { n: "01", t: "Nos conocemos", d: "Hablamos por WhatsApp o llamada para entender tu academia, tus alumnos y sus necesidades." },
              { n: "02", t: "Definimos el modelo", d: "Comisiones por alumno referido, descuentos para tu comunidad, o marca blanca. Lo que mejor encaje." },
              { n: "03", t: "Presentas OKontainers a tus alumnos", d: "Te damos material, casos de uso y soporte para que lo integres en tu formaci\u00f3n." },
              { n: "04", t: "Tus alumnos empiezan a importar", d: "Nos contactan directamente, gestionamos todo y t\u00fa ganas con cada operaci\u00f3n." },
            ] : [
              { n: "01", t: "We get to know each other", d: "We talk via WhatsApp or call to understand your academy, students, and their needs." },
              { n: "02", t: "We define the model", d: "Commissions per referred student, discounts for your community, or white-label. Whatever fits best." },
              { n: "03", t: "You present OKontainers to your students", d: "We provide materials, use cases, and support to integrate into your training." },
              { n: "04", t: "Your students start importing", d: "They contact us directly, we manage everything, and you earn with every operation." },
            ]).map((step, i) => (
              <AnimatedSection key={i} delay={i * 0.1}>
                <div data-testid={`academy-step-${i}`} className="flex gap-5">
                  <div className="shrink-0 w-12 h-12 rounded-full bg-electric flex items-center justify-center shadow-lg shadow-electric/30">
                    <span className="text-white font-bold text-sm font-heading">{step.n}</span>
                  </div>
                  <div className="pt-1">
                    <h3 className="text-lg font-bold text-navy mb-1 font-heading">{step.t}</h3>
                    <p className="text-slate-500 text-sm font-body">{step.d}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA + Form */}
      <section data-testid="academy-form-section" className="bg-navy py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0"><div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full bg-electric/10 blur-[100px]" /></div>
        <div className="relative z-10 max-w-3xl mx-auto px-5 md:px-10 text-center">
          <AnimatedSection>
            <h2 className="text-3xl md:text-4xl font-black text-white font-heading mb-4">
              {lang === "es" ? "Hablemos de C\u00f3mo Colaborar" : "Let\u2019s Talk About Collaborating"}
            </h2>
            <p className="text-slate-300 font-body mb-10">
              {lang === "es"
                ? "D\u00e9janos tus datos y te contactamos en 24h para explicarte el modelo."
                : "Leave your details and we\u2019ll contact you within 24h to explain the model."}
            </p>
          </AnimatedSection>
          <AnimatedSection delay={0.2}>
            {status === "success" ? (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white/10 backdrop-blur-lg rounded-2xl p-10 border border-white/10">
                <CheckCircle size={48} className="text-emerald-400 mx-auto mb-4" />
                <p className="text-white text-lg font-semibold font-heading">
                  {lang === "es" ? "Solicitud enviada. Te contactamos pronto!" : "Request sent! We\u2019ll be in touch!"}
                </p>
              </motion.div>
            ) : (
              <form data-testid="academy-form" onSubmit={handleSubmit} className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/10">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <input data-testid="academy-form-name" type="text" required value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder={lang === "es" ? "Tu nombre" : "Your name"}
                    className="w-full bg-white/10 border border-white/15 rounded-xl px-5 py-3.5 text-white placeholder-slate-400 text-sm font-body focus:outline-none focus:border-electric focus:ring-1 focus:ring-electric" />
                  <input data-testid="academy-form-company" type="text" required value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                    placeholder={lang === "es" ? "Nombre de tu academia" : "Academy name"}
                    className="w-full bg-white/10 border border-white/15 rounded-xl px-5 py-3.5 text-white placeholder-slate-400 text-sm font-body focus:outline-none focus:border-electric focus:ring-1 focus:ring-electric" />
                  <input data-testid="academy-form-email" type="email" required value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder={lang === "es" ? "Tu email" : "Your email"}
                    className="w-full bg-white/10 border border-white/15 rounded-xl px-5 py-3.5 text-white placeholder-slate-400 text-sm font-body focus:outline-none focus:border-electric focus:ring-1 focus:ring-electric" />
                </div>
                {status === "error" && (
                  <div className="flex items-center justify-center gap-2 mb-4 text-red-400 text-sm font-body">
                    <AlertCircle size={16} />{lang === "es" ? "Error. Inténtalo de nuevo." : "Error. Please try again."}
                  </div>
                )}
                <button data-testid="academy-form-submit" type="submit" disabled={status === "loading"}
                  className="inline-flex items-center justify-center gap-2 bg-electric text-white font-bold py-4 px-10 rounded-full hover:bg-electric-hover transition-all shadow-xl shadow-electric/30 text-base disabled:opacity-60">
                  {status === "loading" ? <Loader2 size={20} className="animate-spin" /> : <Send size={18} />}
                  {lang === "es" ? "Quiero Colaborar" : "I Want to Collaborate"}
                </button>
              </form>
            )}
          </AnimatedSection>
          <div className="flex flex-wrap justify-center gap-6 mt-8">
            {(lang === "es"
              ? ["Sin compromiso", "Respuesta en 24h", "Modelo flexible"]
              : ["No commitment", "Response in 24h", "Flexible model"]
            ).map((badge, i) => (
              <div key={i} className="flex items-center gap-2">
                <ShieldCheck size={14} className="text-emerald-400" />
                <span className="text-slate-400 text-xs font-medium font-body">{badge}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer minimal */}
      <footer className="bg-navy-dark py-10">
        <div className="max-w-7xl mx-auto px-5 md:px-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="OKontainers" className="h-8 w-auto brightness-0 invert" />
            <span className="text-slate-500 text-xs font-body">&copy; {new Date().getFullYear()} OKontainers</span>
          </div>
          <a href="/" className="text-slate-400 hover:text-white text-sm font-body transition-colors">
            {lang === "es" ? "\u2190 Volver a okontainers.com" : "\u2190 Back to okontainers.com"}
          </a>
        </div>
      </footer>
    </div>
  );
};

export default AcademyLandingPage;
